import type { PackageNotificationPackageType } from "../../domain/contracts/triggers";
import { Badge, cn } from "@devhop/ui";
import { TRIGGER_LABELS } from "./WorkerStatusBanner";

export function formatNotificationDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return value.slice(0, 16).replace("T", " ");
}

export function PackageTypeBadge({
  packageType,
}: {
  packageType: PackageNotificationPackageType;
}) {
  if (packageType === "package") {
    return (
      <Badge
        variant="outline"
        className="border-blue-500/40 bg-blue-500/10 text-blue-700"
      >
        ຄລາສ
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      className="border-violet-500/40 bg-violet-500/10 text-violet-700"
    >
      ສ່ວນຕົວ
    </Badge>
  );
}

export function TriggerBadge({ triggerType }: { triggerType: string }) {
  const label = TRIGGER_LABELS[triggerType] ?? triggerType;

  const className =
    triggerType === "expiring_soon"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-800"
      : triggerType === "combined"
        ? "border-purple-500/40 bg-purple-500/10 text-purple-700"
        : "border-slate-500/30 bg-muted/60 text-foreground";

  return (
    <Badge variant="outline" className={className}>
      {label}
    </Badge>
  );
}

const LOG_STATUS_LABELS: Record<string, string> = {
  sent: "ສົ່ງແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  skipped: "ຂ້າມ",
};

export function LogStatusBadge({ status }: { status: string }) {
  const label = LOG_STATUS_LABELS[status] ?? status;

  if (status === "sent") {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
      >
        {label}
      </Badge>
    );
  }

  if (status === "failed") {
    return <Badge variant="destructive">{label}</Badge>;
  }

  return <Badge variant="secondary">{label}</Badge>;
}

const QUEUE_STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຖ້າ",
  processing: "ກຳລັງສົ່ງ",
  sent: "ສົ່ງແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  skipped: "ຂ້າມ",
  cancelled: "ຍົກເລີກ",
};

export function QueueStatusBadge({ status }: { status: string }) {
  const variant =
    status === "sent"
      ? "default"
      : status === "failed"
        ? "destructive"
        : status === "pending"
          ? "secondary"
          : "outline";

  const className =
    status === "pending"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-800"
      : status === "processing"
        ? "border-blue-500/40 bg-blue-500/10 text-blue-700"
        : undefined;

  return (
    <Badge variant={variant} className={cn(className)}>
      {QUEUE_STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

export const LOG_STATUS_FILTER_OPTIONS = [
  { key: "all", label: "ທັງໝົດ" },
  { key: "sent", label: "ສົ່ງແລ້ວ" },
  { key: "failed", label: "ລົ້ມເຫຼວ" },
  { key: "skipped", label: "ຂ້າມ" },
] as const;

export const QUEUE_STATUS_FILTER_OPTIONS = [
  { key: "all", label: "ທັງໝົດ" },
  { key: "pending", label: "ລໍຖ້າ" },
  { key: "processing", label: "ກຳລັງສົ່ງ" },
  { key: "failed", label: "ລົ້ມເຫຼວ" },
  { key: "sent", label: "ສົ່ງແລ້ວ" },
] as const;
